import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronRight, CloudOff } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime } from './relative-time';
import { useIsMobile } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import type { LicenseRequest, LicenseRequestStatus } from '@/schemas/license-requests';

const STATUS_TEXT_CLASS: Record<LicenseRequestStatus, string> = {
  submitted: 'text-status-pending',
  approved: 'text-status-active',
  rejected: 'text-status-revoked',
};

const STATUS_DOT_CLASS: Record<LicenseRequestStatus, string> = {
  submitted: 'bg-status-pending',
  approved: 'bg-status-active',
  rejected: 'bg-status-revoked',
};

const STATUS_LABEL_KEYS: Record<LicenseRequestStatus, string> = {
  submitted: 'requests.status.submitted',
  approved: 'requests.status.approved',
  rejected: 'requests.status.rejected',
};

/**
 * Human-readable submitter for the queue: operator id for human submissions,
 * "Machine (<integration>)" for machine (CRM token) submissions, dash when
 * neither is present. Machine rows have submittedBy = null — the source must
 * stay visible to the reviewer instead of rendering an empty cell.
 */
export function submitterLabel(request: LicenseRequest, t: TFunction): string {
  if (request.submittedBy) return request.submittedBy;
  if (request.submittedByIntegration) {
    return t('requests.queue.machineSubmitter', { integration: request.submittedByIntegration });
  }
  return '—';
}

export function RequestStatusWord({ status }: { status: LicenseRequestStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-medium ${STATUS_TEXT_CLASS[status]}`}
    >
      <span className={`h-2 w-2 rounded-full ${STATUS_DOT_CLASS[status]}`} aria-hidden="true" />
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}

export function OfflineFlag({ request }: { request: LicenseRequest }) {
  const { t } = useTranslation();
  if (!request.offlineRequested) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-medium text-status-pending"
            aria-label={t('requests.queue.offlineAria')}
          >
            <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
            {t('requests.queue.offlineBadge')}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">
            {t('requests.queue.offlineTooltip')}{' '}
            {request.offlineRequestedAt ? <RelativeTime date={request.offlineRequestedAt} /> : null}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface RequestsQueueTableProps {
  data: LicenseRequest[];
  onSelect: (request: LicenseRequest) => void;
}

export function RequestsQueueTable({ data, onSelect }: RequestsQueueTableProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <ul className="border-t">
        {data.map((request) => (
          <li key={request.id} className="border-b">
            <button
              type="button"
              onClick={() => onSelect(request)}
              className={cn(
                'flex w-full items-center gap-3 px-1 py-4 text-left transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                request.status === 'submitted' && 'bg-status-pending/[0.04]'
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[13.5px] font-medium text-foreground">
                  {request.serial}
                </span>
                <span className="mt-1 block truncate text-[13px] text-faint">
                  {request.buyerCompany} · <RelativeTime date={request.createdAt} />
                </span>
              </span>
              <OfflineFlag request={request} />
              <RequestStatusWord status={request.status} />
              <ChevronRight className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-background">
        <TableRow className="bg-foreground/[0.03] hover:bg-foreground/[0.03]">
          <TableHead className="h-11 px-3 text-[13px] font-medium text-faint">
            {t('requests.queue.serial')}
          </TableHead>
          <TableHead className="h-11 px-3 text-[13px] font-medium text-faint">
            {t('requests.queue.buyer')}
          </TableHead>
          <TableHead className="h-11 px-3 text-[13px] font-medium text-faint">
            {t('requests.queue.submitter')}
          </TableHead>
          <TableHead className="h-11 px-3 text-[13px] font-medium text-faint">
            {t('requests.queue.status')}
          </TableHead>
          <TableHead className="h-11 px-3 text-[13px] font-medium text-faint">
            {t('requests.queue.offline')}
          </TableHead>
          <TableHead className="h-11 px-3 text-[13px] font-medium text-faint">
            {t('requests.queue.created')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((request) => (
          <TableRow
            key={request.id}
            tabIndex={0}
            className={cn(
              'group/row cursor-pointer hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              // Pending (submitted) requests are the work queue — visually prioritized.
              request.status === 'submitted' && 'bg-status-pending/[0.05]'
            )}
            onClick={() => onSelect(request)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSelect(request);
            }}
            data-testid={`request-row-${request.id}`}
          >
            <TableCell className="px-3 py-3">
              <span className="block max-w-[220px] truncate font-mono text-[13.5px] font-medium text-foreground">
                {request.serial}
              </span>
            </TableCell>
            <TableCell className="px-3 py-3">
              <span className="block max-w-[200px] truncate text-[13.5px] text-muted-foreground">
                {request.buyerCompany}
              </span>
            </TableCell>
            <TableCell className="px-3 py-3">
              <span className="block max-w-[160px] truncate font-mono text-[12.5px] text-muted-foreground">
                {submitterLabel(request, t)}
              </span>
            </TableCell>
            <TableCell className="px-3 py-3">
              <RequestStatusWord status={request.status} />
            </TableCell>
            <TableCell className="px-3 py-3">
              <OfflineFlag request={request} />
            </TableCell>
            <TableCell className="px-3 py-3">
              <span className="text-[13.5px] text-muted-foreground">
                <RelativeTime date={request.createdAt} />
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
